import * as z from "zod";
import { dropWorkflowConnectorUsages } from "../connectorUsages";
import { findUniqueName } from "../findUniqueName";
import { zql } from "../generatedDrizzleSchema";
import { isSupplierAvailable } from "../rbac/crossResourceAvailability";
import { grantsForScope } from "../rbac/grantQueries";
import { uniqueWorkflowName } from "../workflowName";
import { assertCanChangeLiveness } from "./livenessGate";
import { mutator } from "./mutator";

export const moveWorkflow = mutator({
  schema: z.object({ workflowId: z.string(), spaceId: z.string() }),
  resolve: {
    workflow: { kind: "workflow", from: "workflowId" },
    targetSpace: { kind: "space", from: "spaceId" },
  },
  permissions: [
    {
      permission: "space:write",
      scopeId: ({ args }) => args.workflow.spaceId,
    },
    {
      permission: "space:write",
      scopeId: ({ args }) => args.targetSpace.id,
    },
  ],
  fn: async ({ tx, ctx, args: { workflow, targetSpace } }) => {
    let name = workflow.name;
    if (workflow.spaceId !== targetSpace.id) {
      await assertCanChangeLiveness(tx, ctx, workflow);

      const targetWorkflows = await tx.run(
        zql.workflows
          .where("spaceId", targetSpace.id)
          .where("deletedAt", "IS", null)
      );
      name = uniqueWorkflowName(
        workflow.name,
        targetWorkflows.map((w) => w.name)
      );

      // Connectors the target space can't use are disconnected rather than
      // blocking the move; affected steps keep a placeholder until a
      // replacement is associated. A grantless target offers no connector
      // access here (matching the move modal's warning) even though the
      // availability rule treats it as vacuously open for tenant admins.
      const appMappings = await tx.run(
        zql.connectedAppUsages
          .where("type", "workflow_slug")
          .where("refId", workflow.id)
      );
      if (appMappings.length > 0) {
        const targetGrants = await tx.run(
          grantsForScope(targetSpace.id, targetSpace.tenantId)
        );
        for (const mapping of appMappings) {
          const available =
            targetGrants.length > 0 &&
            (await isSupplierAvailable({
              tx,
              tenantId: ctx.tenantId,
              ruleId: "space_uses_connected_apps",
              dependentScopeId: targetSpace.id,
              supplierScopeId: mapping.connectedAppId,
            }));
          if (!available) {
            await dropWorkflowConnectorUsages(
              tx,
              mapping.connectedAppId,
              workflow.id
            );
          }
        }
      }

      // Liveness is granted per space via space:push_live, so a move must not
      // carry it into the target space: the live version becomes a draft owned
      // by the mover, to be pushed live again in the new space.
      const [branches, [sourceSpace]] = await Promise.all([
        tx.run(zql.workflowBranches.where("workflowId", workflow.id)),
        tx.run(zql.spaces.where("id", workflow.spaceId)),
      ]);
      const mainBranch = branches.find((b) => b.name === "main");
      if (mainBranch) {
        await tx.mutate.workflowBranches.update({
          id: mainBranch.id,
          name: findUniqueName(
            `Live from ${sourceSpace!.name}`,
            new Set(branches.map((b) => b.name))
          ),
          userId: ctx.userId,
        });
      }
    }

    await tx.mutate.workflows.update({
      id: workflow.id,
      spaceId: targetSpace.id,
      name,
    });
  },
});
